/**
 * Éditeur du bloc `clue_hunt` (chasse aux indices).
 *
 * Mobile-first. Un bloc = intro + timeout + N indices. Chaque indice a un
 * `source_text` (substring EXACTE présente dans les blocs ancêtres) que le
 * tuteur choisit via une modale de sélection texte, un `revealed_md` (KaTeX)
 * et un `xp_reward`.
 *
 * Sources dispo : tous les `statement`, la `statement_question` parente et
 * celles qui la précèdent (par position), et les `correction.answer_md` des
 * questions précédentes. Cf. edge fn `exercise-v2-clue-hunt`.
 */

import { useMemo, useState } from 'react';
import { Plus, Trash2, MousePointer, AlertTriangle } from 'lucide-react';
import { MarkdownMath, cn } from '@jeraisonne/ui';
import type { BlockComponentProps, ExerciseBlock } from '../types';
import type { Clue, ClueHuntData, ClueHuntSourceCandidate } from '@jeraisonne/db';
import { verifyClueSourceText } from '@jeraisonne/db';
import { SelectClueSourceModal } from './SelectClueSourceModal';

const DEFAULT_TIMEOUT_MS = 30_000;

function readData(block: ExerciseBlock): ClueHuntData {
  const d = block.data as Partial<ClueHuntData>;
  return {
    intro_md: typeof d.intro_md === 'string' ? d.intro_md : "Trouve les indices dans l'énoncé",
    timeout_ms: typeof d.timeout_ms === 'number' ? d.timeout_ms : DEFAULT_TIMEOUT_MS,
    clues: Array.isArray(d.clues) ? (d.clues as Clue[]) : [],
  };
}

function nextClueId(clues: readonly Clue[]): string {
  let n = clues.length + 1;
  const taken = new Set(clues.map((c) => c.id));
  while (taken.has(`clue_${n}`)) n += 1;
  return `clue_${n}`;
}

/**
 * Construit les sources cliquables (mêmes règles que l'edge fn) : statement +
 * statement_question de la parente et précédentes + correction.answer_md des
 * questions précédentes. `allBlocks` requis.
 */
function buildSources(
  block: ExerciseBlock,
  allBlocks: readonly ExerciseBlock[],
): ClueHuntSourceCandidate[] {
  const orderedQuestions = allBlocks
    .filter((b) => b.section === 'statement' && b.block_type === 'statement_question')
    .sort((a, b) => a.position - b.position);
  const parentIndex = orderedQuestions.findIndex((q) => q.id === block.parent_id);
  const cutoff = parentIndex < 0 ? orderedQuestions.length : parentIndex + 1;

  const statements: ClueHuntSourceCandidate[] = allBlocks
    .filter((b) => b.section === 'statement' && b.block_type === 'statement')
    .sort((a, b) => a.position - b.position)
    .flatMap((b, i) => {
      const md = (b.data as { content_md?: string }).content_md ?? '';
      return md.trim()
        ? [{ id: b.id, kind: 'statement' as const, content_md: md, label: `Énoncé ${i + 1}` }]
        : [];
    });

  const questions: ClueHuntSourceCandidate[] = orderedQuestions.slice(0, cutoff).flatMap((q, i) => {
    const md = (q.data as { prompt_md?: string }).prompt_md ?? '';
    return md.trim()
      ? [
          {
            id: q.id,
            kind: 'statement_question' as const,
            content_md: md,
            label: `Question ${i + 1}`,
          },
        ]
      : [];
  });

  const questionIndexById = new Map(orderedQuestions.map((q, i) => [q.id, i]));
  const corrections: ClueHuntSourceCandidate[] = allBlocks
    .filter(
      (b) => b.section === 'correction' && b.block_type === 'correction' && b.parent_id !== null,
    )
    .flatMap((c) => {
      const qi = questionIndexById.get(c.parent_id!);
      if (qi === undefined || qi >= parentIndex) return [];
      const answer = (c.data as { answer_md?: string }).answer_md ?? '';
      return answer.trim()
        ? [
            {
              id: c.id,
              kind: 'correction' as const,
              content_md: answer,
              label: `Résultat Q${qi + 1}`,
            },
          ]
        : [];
    });

  return [...statements, ...questions, ...corrections];
}

export function ClueHuntBlockEditor(props: BlockComponentProps) {
  const { block, questions, isDisabled, onPatchData, onPatchParent, allBlocks = [] } = props;
  const data = readData(block);

  const sources = useMemo(() => buildSources(block, allBlocks), [block, allBlocks]);
  const [selectingIndex, setSelectingIndex] = useState<number | null>(null);

  function patchClue(index: number, patch: Partial<Clue>) {
    const clues = data.clues.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onPatchData({ ...data, clues });
  }
  function addClue() {
    onPatchData({
      ...data,
      clues: [
        ...data.clues,
        {
          id: nextClueId(data.clues),
          source_text: '',
          revealed_md: '',
          xp_reward: 10,
        },
      ],
    });
  }
  function removeClue(index: number) {
    onPatchData({ ...data, clues: data.clues.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-3">
      {/* Rattachement (parent_id → statement_question) */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-600">Rattachée à la question</label>
        <select
          value={block.parent_id ?? ''}
          onChange={(e) => onPatchParent(e.target.value || null)}
          disabled={isDisabled}
          className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
        >
          <option value="" disabled>
            — sélectionner —
          </option>
          {questions.map((q) => (
            <option key={q.id} value={q.id}>
              {((q.data as { prompt_md?: string }).prompt_md ?? '(sans titre)').slice(0, 60)}
            </option>
          ))}
        </select>
      </div>

      {/* intro_md */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-600">Consigne (intro)</label>
        <input
          type="text"
          value={data.intro_md}
          onChange={(e) => onPatchData({ ...data, intro_md: e.target.value })}
          disabled={isDisabled}
          className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
          maxLength={512}
        />
      </div>

      {/* timeout_ms */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-600">
          Chrono par indice (partagé, redémarre à chaque validation)
        </label>
        <select
          value={data.timeout_ms}
          onChange={(e) => onPatchData({ ...data, timeout_ms: Number(e.target.value) })}
          disabled={isDisabled}
          className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm sm:w-48"
        >
          <option value={15_000}>15 secondes</option>
          <option value={30_000}>30 secondes</option>
          <option value={45_000}>45 secondes</option>
          <option value={60_000}>60 secondes</option>
        </select>
      </div>

      {/* Liste des indices */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-slate-600">
            Indices ({data.clues.length}/6)
          </label>
          <button
            type="button"
            disabled={isDisabled || data.clues.length >= 6}
            onClick={addClue}
            className={cn(
              'flex min-h-11 items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 active:scale-[0.98]',
              (isDisabled || data.clues.length >= 6) && 'opacity-40',
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            Ajouter un indice
          </button>
        </div>

        {data.clues.length === 0 && (
          <p className="text-xs text-slate-500">
            Ajoute au moins un indice. Chaque indice pointe vers un mot ou groupe de mots présent
            dans l'énoncé ou dans les résultats des questions précédentes.
          </p>
        )}

        <ul className="space-y-3">
          {data.clues.map((clue, index) => {
            const verdict = verifyClueSourceText(clue.source_text, sources, clue.source_block_id);
            const badgeColor =
              verdict.kind === 'unique'
                ? 'bg-emerald-100 text-emerald-800'
                : verdict.kind === 'none'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-amber-100 text-amber-800';
            const badgeLabel =
              verdict.kind === 'unique'
                ? '1 match'
                : verdict.kind === 'none'
                  ? '⚠ aucun match'
                  : `${verdict.occurrences.length} matches`;

            return (
              <li key={clue.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    Indice #{index + 1}
                  </span>
                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={() => removeClue(index)}
                    className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-200 hover:text-red-600 active:scale-[0.98]"
                    aria-label={`Supprimer l'indice ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* source_text + bouton "Sélectionner" */}
                <div className="mb-2 space-y-1">
                  <label className="flex items-center justify-between text-xs font-medium text-slate-600">
                    <span>Texte à cliquer (dans l'énoncé)</span>
                    <span className={cn('rounded-full px-2 py-0.5 text-[10px]', badgeColor)}>
                      {badgeLabel}
                    </span>
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={clue.source_text}
                      onChange={(e) => patchClue(index, { source_text: e.target.value })}
                      disabled={isDisabled}
                      placeholder="ex. AB = 4"
                      className="min-h-11 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 font-mono text-xs sm:text-sm"
                      maxLength={256}
                    />
                    <button
                      type="button"
                      disabled={isDisabled || sources.length === 0}
                      onClick={() => setSelectingIndex(index)}
                      className={cn(
                        'flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 active:scale-[0.98] sm:w-auto',
                        (isDisabled || sources.length === 0) && 'opacity-40',
                      )}
                    >
                      <MousePointer className="h-3.5 w-3.5" />
                      Sélectionner
                    </button>
                  </div>
                  {clue.source_block_id && (
                    <p className="text-[11px] text-amber-800">
                      🔒 restreint à un bloc source précis (ambiguïté levée)
                    </p>
                  )}
                </div>

                {/* revealed_md + preview */}
                <div className="mb-2 space-y-1">
                  <label className="block text-xs font-medium text-slate-600">
                    Ce qui s'affiche quand trouvé (KaTeX ok)
                  </label>
                  <textarea
                    value={clue.revealed_md}
                    onChange={(e) => patchClue(index, { revealed_md: e.target.value })}
                    disabled={isDisabled}
                    placeholder="ex. $AB = 4\,\text{cm}$"
                    rows={2}
                    className="min-h-[3rem] w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 font-mono text-xs sm:text-sm"
                    maxLength={512}
                  />
                  {clue.revealed_md.trim() && (
                    <div className="prose prose-sm max-w-none rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-sm">
                      <MarkdownMath>{clue.revealed_md}</MarkdownMath>
                    </div>
                  )}
                </div>

                {/* xp_reward */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-slate-600">
                    XP (divisé /2 à chaque mauvais clic, plancher 1)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={40}
                    value={clue.xp_reward}
                    onChange={(e) =>
                      patchClue(index, {
                        xp_reward: Math.max(1, Math.min(40, Number(e.target.value) || 1)),
                      })
                    }
                    disabled={isDisabled}
                    className="min-h-11 w-24 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
                  />
                </div>
              </li>
            );
          })}
        </ul>

        {sources.length === 0 && data.clues.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Aucun bloc source disponible : ajoute un énoncé ou raccroche ce bloc à une question
              qui a du contenu.
            </span>
          </div>
        )}
      </div>

      {selectingIndex !== null && (
        <SelectClueSourceModal
          sources={sources}
          onClose={() => setSelectingIndex(null)}
          onSelected={(r) => {
            patchClue(selectingIndex, {
              source_text: r.source_text,
              source_block_id: r.source_block_id,
            });
          }}
        />
      )}
    </div>
  );
}
